import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MDTypography from 'components/MDTypography';
import CustomInputForm from 'components/CustomInputForm';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";
import { validatePersonOrEntityName } from "utils/crmValidation";

export const CONTACT_EMAIL_PHONE_REQUIRED = 'Either Email or Phone is required';

const AddContactModal = ({ isOpen, onClose, accountId, accountName, onContactAdded }) => {
  const { user, axiosInstance } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successSnackbar, setSuccessSnackbar] = useState(false);
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      setErrors({});
      setErrorSnackbar(false);
    }
  }, [isOpen]);

  const fields = [
    { name: 'first_name', label: 'First Name', type: 'text', required: true },
    { name: 'last_name', label: 'Last Name', type: 'text', required: true },
    { name: 'email', label: 'Email', type: 'text', inputType: 'email'},
    {
      name: 'phone',
      label: 'Phone',
      type: 'tel',
      inputType: 'tel',
      pattern: '[0-9]{3}-[0-9]{3}-[0-9]{4}',
    }
  ];

  const handleSubmit = async (data) => {
    setIsLoading(true);
    setErrors({});

    const fieldErrors = {};

    if (!data.first_name || !data.first_name.trim()) {
      fieldErrors.first_name = 'First Name is required.';
    } else {
      const firstNameErr = validatePersonOrEntityName(data.first_name, 'First name');
      if (firstNameErr) fieldErrors.first_name = firstNameErr;
    }

    if (!data.last_name || !data.last_name.trim()) {
      fieldErrors.last_name = 'Last Name is required.';
    } else {
      const lastNameErr = validatePersonOrEntityName(data.last_name, 'Last name');
      if (lastNameErr) fieldErrors.last_name = lastNameErr;
    }

    const trimmedEmail = (data.email || '').trim();
    const trimmedPhone = (data.phone || '').trim();
    if (!trimmedEmail && !trimmedPhone) {
      fieldErrors.email = CONTACT_EMAIL_PHONE_REQUIRED;
      fieldErrors.phone = CONTACT_EMAIL_PHONE_REQUIRED;
    } else {
      // Same regexes as ContactFormModal.getFormErrors, so both entry
      // points agree on what counts as a valid email/phone.
      if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
        fieldErrors.email = 'Please enter a valid email address';
      }
      if (trimmedPhone && !/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/.test(trimmedPhone)) {
        fieldErrors.phone = 'Please enter a valid phone number';
      }
    }

    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      setIsLoading(false);
      return;
    }

    const sanitizedData = {
      first_name: data.first_name || '',
      last_name: data.last_name,
      email: data.email || '',
      phone: data.phone || '',
      account_name: accountName,
      created_by: user.id,
      updated_by: user.id,
      created_by_name: user.username,
      updated_by_name: user.username,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    try {
      const response = await axiosInstance.post('/contacts/', {
        ...sanitizedData,
        account_id: accountId,
        account_name: accountName,
        primary_contact: false,
      });
      setSuccessSnackbar(true);
      onClose();
      await onContactAdded();
    } catch (err) {
      console.error('Error creating contact:', err);
      // Same pattern as ContactFormModal.handleSaveContact and
      // ContactDetail.handleCloneSave: a backend rejection (e.g. duplicate
      // email) should surface inline on the Email field, not just as a
      // generic snackbar that throws away the actual reason.
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'string' && detail.toLowerCase().includes('email')) {
        setErrors((prev) => ({ ...prev, email: detail }));
      } else {
        setErrorMessage('Failed to create contact. Please try again.');
        setErrorSnackbar(true);
      }
    } finally {
      setIsLoading(false);
    }
  };



  const handleCloseSuccessSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSuccessSnackbar(false);
  };

  const handleCloseErrorSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setErrorSnackbar(false);
  };

  // Logic to handle the Cancel button click
  const handleCancel = () => {
    onClose(); // Close the modal when the Cancel button is clicked
  };

  const handleFormFieldChange = (e) => {
    const { name, value } = e.target || {};
    if (!name) return;

    setErrors((prev) => {
      if (!prev[name] && prev.email !== CONTACT_EMAIL_PHONE_REQUIRED && prev.phone !== CONTACT_EMAIL_PHONE_REQUIRED) {
        return prev;
      }
      const next = { ...prev };
      delete next[name]; // this field's own error is stale now that it's being edited

      // Cross-field: once either email or mobile has content, the pairing
      // requirement no longer applies to the other field — but only clear it
      // if that's actually the error showing there, so a genuine format
      // error (e.g. bad email) survives.
      if ((name === 'email' || name === 'phone') && value && value.trim()) {
        const other = name === 'email' ? 'phone' : 'email';
        if (next[other] === CONTACT_EMAIL_PHONE_REQUIRED) {
          delete next[other];
        }
      }
      return next;
    });
  };

  return (
      <>
        <Modal
            open={isOpen}
            onClose={handleCancel}
            aria-labelledby="contact-modal-title"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {/* Header */}
            <Box className="pearl-popup-header"
                sx={{
                  backgroundColor: colors.popupHeader.background,
                  px: 4,
                  py: 3,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
            >
              <MDTypography
                  id="contact-modal-title"
                  variant="h6"
                  component="h2"
                  sx={{ color: '#FFFFFF' }}
              >
                Add Contact
              </MDTypography>
              <IconButton
                  aria-label="Close"
                  onClick={onClose}
                  sx={{ color: '#FFFFFF', ml: 1 }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ p: 4, position: 'relative' }}>
              {/* Keep the form mounted while saving: CustomInputForm holds its
                  values in local state, so unmounting it would wipe what the
                  user typed before a server-side error (e.g. duplicate email). */}
              <Box onChange={handleFormFieldChange} sx={{ pointerEvents: isLoading ? 'none' : 'auto' }}>
                <CustomInputForm
                    fields={fields}
                    onSubmit={handleSubmit}
                    onCancel={handleCancel}
                    errors={errors}
                    disableNativeValidation
                />
              </Box>
              {isLoading && (
                  <Box
                      position="absolute"
                      sx={{ inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.6)' }}
                  >
                    <CircularProgress />
                  </Box>
              )}
            </Box>
          </Box>
        </Modal>

        <Snackbar
            open={successSnackbar}
            autoHideDuration={6000}
            onClose={handleCloseSuccessSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSuccessSnackbar} severity="success" sx={{ width: '100%' }}>
            Contact created successfully!
          </Alert>
        </Snackbar>

        <Snackbar
            open={errorSnackbar}
            autoHideDuration={6000}
            onClose={handleCloseErrorSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseErrorSnackbar} severity="error" sx={{ width: '100%' }}>
            {errorMessage}
          </Alert>
        </Snackbar>
      </>
  );
};

export default AddContactModal;